import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logout } from '@samparkchat/sampark-web-chat/sampark/sampark-chat/screens/VisitorBotChatWidget';
import { User } from '../types';
import usersData from '../data/mock-users.json';

interface AuthContextType {
  user: User | null;
  login: (userid: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (userData: Omit<User, 'id'> & { password: string }) => Promise<boolean>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const mockUsers: (User & { password: string; access_token?: string })[] = [
  ...usersData.doctors.map(doctor => ({
    id: doctor.id,
    userid: doctor.userid,
    name: doctor.name,
    email: doctor.email,
    password: doctor.password,
    role: 'doctor' as const,
    specialization: doctor.specialization,
    avatar: doctor.avatar,
    verified: doctor.verified,
    phone: doctor.phone,
    accessToken: (doctor as any).access_token || `demo-jwt-${doctor.userid}`,
  }))
];

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (userid: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const foundUser = mockUsers.find(u => 
      u.userid === userid && u.password === password
    );
    
    if (foundUser) {
      const { password: _, access_token: __, ...rest } = foundUser as any;
      const userWithoutPassword: User = {
        ...rest,
        accessToken: foundUser.accessToken || (foundUser as any).access_token,
      };
      setUser(userWithoutPassword);
      localStorage.setItem('user', JSON.stringify(userWithoutPassword));
      setIsLoading(false);
      return true;
    }
    
    setIsLoading(false);
    return false;
  };

  const register = async (userData: Omit<User, 'id'> & { password: string }): Promise<boolean> => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check if userid already exists
    const existingUser = mockUsers.find(u => u.userid === userData.userid);
    if (existingUser) {
      setIsLoading(false);
      return false;
    }
    
    // Create new user
    const newUser: User = {
      id: Date.now().toString(),
      userid: userData.userid,
      name: userData.name,
      email: userData.email,
      role: 'doctor',
      specialization: userData.specialization,
      phone: userData.phone,
      verified: true,
      accessToken: `demo-jwt-${userData.userid}`,
    };
    
    // Add to mock database
    mockUsers.push({ ...newUser, password: userData.password });
    
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
    setIsLoading(false);
    return true;
  };

  const handleLogout = async () => {
    await logout().catch(() => undefined);
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout:handleLogout, register, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};